/**
 * Chain Constants - Centralized chain identifiers and metadata
 * Follow industry best practice: Single source of truth for all chain-related constants
 */

import {
  arbitrum,
  avalanche,
  base,
  blast,
  bsc,
  linea,
  mainnet,
  manta,
  mode,
  optimism,
  scroll,
  sonic,
  tac,
  unichain,
  xLayer,
  zircuit,
  zksync,
  plasma,
  stable,
  sepolia,
  megaeth,
  monad,
  polygon,
  mantle,
  ink,
} from "viem/chains";

// Chain IDs - Use enum for type safety and autocomplete
export enum ChainId {
  ETHEREUM = mainnet.id,
  OPTIMISM = optimism.id,
  BSC = bsc.id,
  POLYGON = polygon.id,
  BASE = base.id,
  ARBITRUM = arbitrum.id,
  AVALANCHE = avalanche.id,
  LINEA = linea.id,
  MODE = mode.id,
  BLAST = blast.id,
  SCROLL = scroll.id,
  ZKSYNC = zksync.id,
  XLAYER = xLayer.id,
  MANTA = manta.id,
  ZIRCUIT = zircuit.id,
  TAC = tac.id,
  UNICHAIN = unichain.id,
  SONIC = sonic.id,
  PLASMA = plasma.id,
  STABLE = stable.id,
  MANTLE = mantle.id,
  INK = ink.id,
  MONAD = monad.id,
  MEGAETH = megaeth.id,
  SEPOLIA = sepolia.id, // Testnet
}

// Chain Display Names
export const CHAIN_NAMES: Record<ChainId, string> = {
  [ChainId.ETHEREUM]: "Ethereum",
  [ChainId.OPTIMISM]: "Optimism",
  [ChainId.BSC]: "BNB Chain",
  [ChainId.POLYGON]: "Polygon",
  [ChainId.BASE]: "Base",
  [ChainId.ARBITRUM]: "Arbitrum",
  [ChainId.AVALANCHE]: "Avalanche",
  [ChainId.LINEA]: "Linea",
  [ChainId.MODE]: "Mode",
  [ChainId.BLAST]: "Blast",
  [ChainId.SCROLL]: "Scroll",
  [ChainId.ZKSYNC]: "zkSync Era",
  [ChainId.XLAYER]: "X Layer",
  [ChainId.MANTA]: "Manta Pacific",
  [ChainId.ZIRCUIT]: "Zircuit",
  [ChainId.TAC]: "TAC",
  [ChainId.UNICHAIN]: "Unichain",
  [ChainId.SONIC]: "Sonic",
  [ChainId.PLASMA]: "Plasma",
  [ChainId.STABLE]: "Stable",
  [ChainId.MANTLE]: "Mantle",
  [ChainId.INK]: "Ink",
  [ChainId.MONAD]: "Monad",
  [ChainId.MEGAETH]: "MegaETH",
  [ChainId.SEPOLIA]: "Sepolia",
};

// Chain Icons - Centralized asset paths
export const CHAIN_ICONS: Record<ChainId, string> = {
  [ChainId.ETHEREUM]: "/assets/chains/ethereum.svg",
  [ChainId.OPTIMISM]: "/assets/chains/optimism.svg",
  [ChainId.BSC]: "/assets/chains/bsc.svg",
  [ChainId.POLYGON]: "/assets/chains/polygon.svg",
  [ChainId.BASE]: "/assets/chains/base.svg",
  [ChainId.ARBITRUM]: "/assets/bridge/arbitrum.svg",
  [ChainId.AVALANCHE]: "/assets/chains/avalanche.png",
  [ChainId.LINEA]: "/assets/chains/linea.svg",
  [ChainId.MODE]: "/assets/chains/mode.svg",
  [ChainId.BLAST]: "/assets/bridge/blast.svg",
  [ChainId.SCROLL]: "/assets/chains/scroll.svg",
  [ChainId.ZKSYNC]: "/assets/chains/zksync.svg",
  [ChainId.XLAYER]: "/assets/chains/xlayer.svg",
  [ChainId.MANTA]: "/assets/bridge/manta-pacific.svg",
  [ChainId.ZIRCUIT]: "/assets/chains/zircuit.svg",
  [ChainId.TAC]: "/assets/chains/tac.svg",
  [ChainId.UNICHAIN]: "/assets/chains/unichain.svg",
  [ChainId.SONIC]: "/assets/chains/sonic.png",
  [ChainId.PLASMA]: "/assets/chains/plasma.png",
  [ChainId.SEPOLIA]: "/assets/chains/ethereum.svg",
  [ChainId.STABLE]: "/assets/chains/stable.webp",
  [ChainId.MANTLE]: "/assets/chains/mantle.svg",
  [ChainId.INK]: "/assets/chains/ink.svg",
  [ChainId.MONAD]: "/assets/chains/monad.png",
  [ChainId.MEGAETH]: "/assets/chains/megaeth.webp",
};

// Defi section mapping - which chain data to use for DeFi
export const CHAIN_DEFI_SECTION: Record<number, string> = {
  [ChainId.ETHEREUM]: "Ethereum",
  [ChainId.OPTIMISM]: "Optimism",
  [ChainId.SEPOLIA]: "Ethereum",
  [ChainId.XLAYER]: "Ethereum",
  [ChainId.ZKSYNC]: "zkSync",
  [ChainId.BASE]: "Base",
  [ChainId.ARBITRUM]: "Arbitrum",
  [ChainId.MODE]: "Mode",
  [ChainId.LINEA]: "Linea",
  [ChainId.BLAST]: "Blast",
  [ChainId.SCROLL]: "Scroll",
  [ChainId.AVALANCHE]: "Avalanche",
  [ChainId.SONIC]: "Sonic",
};

// Chains where minting has limits
export const MINT_LIMIT_CHAINS: ChainId[] = [
  ChainId.SCROLL,
  ChainId.ZKSYNC,
  ChainId.BASE,
  ChainId.OPTIMISM,
  ChainId.LINEA,
  ChainId.ZIRCUIT,
  ChainId.TAC,
  ChainId.SONIC,
  ChainId.PLASMA,
  ChainId.ETHEREUM,
  ChainId.STABLE,
];

// Chains deprecated from rsETH bridging — applies to kelp bridge/wrap routes only.
// Filtering these out of the bridge/wrap selectors leaves:
// Ethereum, Arbitrum, Base, Linea, Ink, Mantle.
// (Swellchain, Hemi, Berachain, HyperEVM, Movement are not in the current bridge
// config, so they need no entry; add their ids here if they are ever onboarded.)
const DEPRECATED_BRIDGE_CHAINS: ChainId[] = [
  ChainId.OPTIMISM,
  ChainId.MANTA,
  ChainId.MODE,
  ChainId.BLAST,
  ChainId.SCROLL,
  ChainId.XLAYER,
  ChainId.ZKSYNC,
  ChainId.ZIRCUIT,
  ChainId.SONIC,
  ChainId.UNICHAIN,
  ChainId.TAC,
  ChainId.AVALANCHE,
  ChainId.PLASMA,
  ChainId.STABLE,
  ChainId.MEGAETH,
  ChainId.MONAD,
];

export const DEPRECATED_BRIDGE_CHAIN_IDS: Set<number> = new Set(
  DEPRECATED_BRIDGE_CHAINS
);

// Helper functions
export const getChainName = (chainId: number): string => {
  return CHAIN_NAMES[chainId as ChainId] || "Unknown Chain";
};

export const getChainIcon = (chainId: number): string => {
  return CHAIN_ICONS[chainId as ChainId] || "/assets/placeholder.svg";
};

export const isValidChain = (chainId: number): boolean => {
  return chainId in ChainId;
};
